import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  LogOut,
  User,
  FileSpreadsheet,
  Bell,
  MessageSquare,
  X,
  Send,
  Settings2
} from 'lucide-react';
import chatService from '../services/chatService';
import toast from 'react-hot-toast';

const Navbar = ({ user, onLogout, sidebarMode = false }) => {
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChatSidebar, setShowChatSidebar] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [allMessages, setAllMessages] = useState([]);
  const [sending, setSending] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/adminportal/dashboard', icon: LayoutDashboard },
    { name: 'Members', href: '/adminportal/members', icon: Users },
    { name: 'Reports', href: '/adminportal/reports', icon: FileSpreadsheet },
    { name: 'Set up', href: '/adminportal/setup', icon: Settings2 },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const loadChatMessages = useCallback(async (memberId) => {
    if (!memberId) return;
    try {
      const thread = await chatService.getMemberChats(memberId);
      setAllMessages(thread);
    } catch (error) {
      console.error('Navbar: Error loading chat thread:', error);
    }
  }, []);

  const loadUnreadMessages = useCallback(async () => {
    try {
      const unread = await chatService.getUnreadMessages();
      const groupedByMember = unread.reduce((acc, msg) => {
        const memberId = msg.member_id;
        if (!acc[memberId]) {
          acc[memberId] = {
            member_id: memberId,
            member_name: msg.member_name,
            member_uan: msg.member_uan,
            messages: [],
            unread_count: 0,
            latest_message: msg,
            latest_timestamp: msg.timestamp
          };
        }
        acc[memberId].messages.push(msg);
        acc[memberId].unread_count += 1;
        if (new Date(msg.timestamp) > new Date(acc[memberId].latest_timestamp)) {
          acc[memberId].latest_message = msg;
          acc[memberId].latest_timestamp = msg.timestamp;
        }
        return acc;
      }, {});

      const groupedArray = Object.values(groupedByMember).sort(
        (a, b) => new Date(b.latest_timestamp) - new Date(a.latest_timestamp)
      );
      setUnreadMessages(groupedArray);
    } catch (error) {
      console.error('Navbar: Error loading unread messages:', error);
    }
  }, []);

  useEffect(() => {
    loadUnreadMessages();
    const interval = setInterval(loadUnreadMessages, 4000);

    const refresh = () => loadUnreadMessages();
    window.addEventListener('newChatMessage', refresh);
    window.addEventListener('adminReplyAdded', refresh);
    window.addEventListener('chatReadUpdated', refresh);
    window.addEventListener('focus', refresh);

    const handleOpenMemberChat = async (e) => {
      const { memberId, memberName, memberUan } = e.detail || {};
      setSelectedMessage({
        member_id: memberId,
        member_name: memberName,
        member_uan: memberUan
      });
      setShowChatSidebar(true);
      setShowNotifications(false);
      setReplyText('');
      await chatService.markMemberRead(memberId);
      await loadChatMessages(memberId);
      await loadUnreadMessages();
      window.dispatchEvent(new CustomEvent('chatReadUpdated', { detail: { memberId } }));
    };
    window.addEventListener('openMemberChat', handleOpenMemberChat);

    return () => {
      clearInterval(interval);
      window.removeEventListener('newChatMessage', refresh);
      window.removeEventListener('adminReplyAdded', refresh);
      window.removeEventListener('chatReadUpdated', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('openMemberChat', handleOpenMemberChat);
    };
  }, [loadUnreadMessages, loadChatMessages]);

  useEffect(() => {
    if (!showChatSidebar || !selectedMessage?.member_id) return undefined;
    const id = selectedMessage.member_id;
    const tick = () => loadChatMessages(id);
    const interval = setInterval(tick, 4000);
    return () => clearInterval(interval);
  }, [showChatSidebar, selectedMessage?.member_id, loadChatMessages]);

  const handleOpenChat = async (groupedMessage) => {
    const memberId = groupedMessage.member_id;
    setSelectedMessage({
      member_id: memberId,
      member_name: groupedMessage.member_name,
      member_uan: groupedMessage.member_uan
    });
    setShowChatSidebar(true);
    setShowNotifications(false);
    setReplyText('');
    await chatService.markMemberRead(memberId);
    await loadChatMessages(memberId);
    await loadUnreadMessages();
    window.dispatchEvent(new CustomEvent('chatReadUpdated', { detail: { memberId } }));
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage?.member_id || sending) return;
    setSending(true);
    try {
      await chatService.addAdminReply(selectedMessage.member_id, replyText.trim(), {
        adminName: user?.username || 'Admin',
        adminUserId: user?.id || null
      });
      setReplyText('');
      await loadChatMessages(selectedMessage.member_id);
      await loadUnreadMessages();
      toast.success('Reply sent — visible to all admins and the member');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = unreadMessages.reduce((sum, group) => sum + group.unread_count, 0);

  const closeChat = () => {
    setShowChatSidebar(false);
    setSelectedMessage(null);
    setReplyText('');
  };

  return (
    <nav className={`${sidebarMode ? 'bg-transparent border-0' : 'bg-white/95 backdrop-blur border-b border-slate-200 sticky top-0 z-40'}`}>
      <div className={sidebarMode ? 'w-full' : 'container mx-auto px-4'}>
        <div className={`flex justify-between ${sidebarMode ? 'h-auto' : 'h-16'}`}>
          {!sidebarMode && (
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-gray-900">PF Claims Management</h1>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isActive(item.href)
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          <div className={`flex items-center space-x-4 ${sidebarMode ? 'ml-auto' : ''}`}>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative inline-flex items-center p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center font-semibold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg z-20 border border-gray-200 max-h-[600px] flex flex-col">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="font-semibold text-gray-900">Member Messages</h3>
                      <button type="button" onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                      {unreadMessages.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">No new messages</div>
                      ) : (
                        unreadMessages.map((grouped) => (
                          <div
                            key={grouped.member_id}
                            className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleOpenChat(grouped)}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className="font-medium text-sm text-gray-900">{grouped.member_name}</div>
                                <div className="text-xs text-gray-500">UAN: {grouped.member_uan}</div>
                              </div>
                              <span className="ml-2 text-xs font-semibold text-red-600">
                                {grouped.unread_count > 9 ? '9+' : grouped.unread_count}
                              </span>
                            </div>
                            <div className="text-sm text-gray-700 mb-2 line-clamp-2">
                              {grouped.latest_message?.message}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(grouped.latest_timestamp).toLocaleString()}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {!sidebarMode && (
              <>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <User className="w-4 h-4" />
                  <span>Welcome, {user?.username}</span>
                </div>
                <button
                  type="button"
                  onClick={onLogout}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showChatSidebar && selectedMessage && (
        <>
          <div className="fixed inset-0 bg-black/50 z-30" onClick={closeChat} />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-40 flex flex-col">
            <div className="bg-primary-600 text-white p-4 flex justify-between items-center">
              <div>
                <h3 className="font-semibold">{selectedMessage.member_name || 'Member'}</h3>
                <p className="text-xs text-primary-100">UAN: {selectedMessage.member_uan || 'N/A'}</p>
              </div>
              <button type="button" onClick={closeChat} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {allMessages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">No messages in this conversation</div>
              ) : (
                allMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm ${
                        msg.sender === 'admin'
                          ? 'bg-primary-600 text-white rounded-br-md'
                          : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <div className={`mt-1 text-[10px] ${msg.sender === 'admin' ? 'text-primary-100' : 'text-slate-400'}`}>
                        {msg.sender === 'admin' && msg.admin_name
                          ? `Replied by: ${msg.admin_name} · `
                          : ''}
                        {new Date(msg.timestamp).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-3 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type a reply…"
                  className="flex-1 p-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  rows={2}
                />
                <button
                  type="button"
                  disabled={sending || !replyText.trim()}
                  onClick={handleSendReply}
                  className="self-end px-3 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
